import { useState, useEffect } from "react";
import {
  X,
  Brain,
  ChevronDown,
  ChevronRight,
  ThumbsUp,
  ThumbsDown,
  CheckCircle2,
  Eye,
  Clock,
  AlertTriangle,
  RotateCw,
  CircleDashed,
  BellOff,
  Loader2,
  UserRound,
  MessageSquarePlus,
  ArrowLeft,
  Activity,
  FileJson,
  Circle,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useAlert,
  useUpdateAlertStatus,
  useAlertFeedback,
  useRetryEnrichment,
  useUpsertNotificationPolicy,
  useAssignAlert,
  useUsers,
  useAddAlertNote,
} from "@/hooks/use-api";
import { useAuth } from "@/hooks/use-auth";
import { cn } from "@/lib/utils";
import { presentBruteForceAlert } from "@/lib/brute-force-alert";
import { BruteForceSummary } from "@/components/alerts/brute-force-summary";
import {
  severityVariant,
  formatAbsoluteTime,
  formatRelativeTime,
  statusBgColors,
} from "@/lib/formatters";
import type { Alert, AlertStatus } from "@/types/api";

// #665: mute durations. Duplicated (4 lines) from alert-policy-tab to keep the
// per-alert action self-contained — extract to a shared util on a 3rd use.
const MUTE_DURATIONS = [
  { value: "permanent", label: "Permanent", days: null },
  { value: "7", label: "7 days", days: 7 },
  { value: "30", label: "30 days", days: 30 },
  { value: "90", label: "90 days", days: 90 },
] as const;

function mutedUntilFromDuration(value: string): string | null {
  const d = MUTE_DURATIONS.find((x) => x.value === value);
  if (!d || d.days == null) return null;
  return new Date(Date.now() + d.days * 86_400_000).toISOString();
}

const MUTE_SELECT_CLASS =
  "mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

interface AlertDetailPanelProps {
  alertId: string | null;
  onClose: () => void;
  presentation?: "dialog" | "workspace";
}

const LEGACY_AI_CONTEXT_KEYS = ["ai_summary", "investigation", "enrichment"];
type DetailTab = "summary" | "evidence" | "activity" | "ai";

export function AlertDetailPanel({
  alertId,
  onClose,
  presentation = "dialog",
}: AlertDetailPanelProps) {
  const { data, isLoading } = useAlert(alertId);
  const updateStatus = useUpdateAlertStatus();
  const feedbackMutation = useAlertFeedback();
  const retryEnrichment = useRetryEnrichment();
  const mutePolicy = useUpsertNotificationPolicy();
  const assignAlert = useAssignAlert();
  const addNote = useAddAlertNote();
  const { data: usersData } = useUsers();
  const { user } = useAuth();
  const [eventsExpanded, setEventsExpanded] = useState(false);
  const [muteOpen, setMuteOpen] = useState(false);
  const [muteDuration, setMuteDuration] = useState("permanent");
  const [muteReason, setMuteReason] = useState("");
  const [noteText, setNoteText] = useState("");
  const [activeTab, setActiveTab] = useState<DetailTab>("summary");

  // Escape key to close
  useEffect(() => {
    if (!alertId) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [alertId, onClose]);

  useEffect(() => {
    setNoteText("");
    setActiveTab("summary");
  }, [alertId]);

  if (!alertId) return null;

  const alert = data?.alert;
  const isWorkspace = presentation === "workspace";
  const assignees = usersData?.users ?? [];
  const currentAssignee = assignees.find((tenantUser) => tenantUser.cognito_sub === user?.sub);
  const authPresentation = alert ? presentBruteForceAlert(alert) : null;

  function handleStatusUpdate(status: AlertStatus) {
    if (!alertId) return;
    updateStatus.mutate({ alertId, status });
  }

  function handleFeedback(feedback: "true_positive" | "false_positive") {
    if (!alertId) return;
    feedbackMutation.mutate({ alertId, feedback });
  }

  function handleAddNote() {
    if (!alertId || !noteText.trim()) return;
    addNote.mutate(
      { alertId, text: noteText.trim() },
      { onSuccess: () => setNoteText("") },
    );
  }

  // #665: mute every channel for this alert's rule. Reuses PUT /policies
  // (scope=rule) — detection keeps running, only the outbound notification stops.
  const canMute =
    !!alert?.rule_id &&
    !!user?.roles?.some((r) => r === "admin" || r === "owner");
  const canAssign = !!user?.roles?.some(
    (role) => role === "admin" || role === "analyst" || role === "owner",
  );

  function handleMute() {
    if (!alert?.rule_id) return;
    mutePolicy.mutate(
      {
        scope: "rule",
        target: alert.rule_id,
        channel: null,
        mode: "mute",
        muted_until: mutedUntilFromDuration(muteDuration),
        reason: muteReason.trim() || null,
      },
      {
        onSuccess: () => {
          setMuteOpen(false);
          setMuteDuration("permanent");
          setMuteReason("");
        },
      },
    );
  }

  return (
    <>
      {/* Backdrop */}
      {!isWorkspace && (
        <div
          className="fixed inset-0 z-40 bg-black/50"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Panel */}
      <div
        role={isWorkspace ? "region" : "dialog"}
        aria-modal={isWorkspace ? undefined : "true"}
        aria-label={alert?.title ?? "Alert details"}
        className={cn(
          "flex h-full max-w-full flex-col bg-background",
          isWorkspace
            ? "relative w-full"
            : "fixed right-0 top-0 z-50 w-[480px] translate-x-0 border-l shadow-xl transition-transform duration-200 animate-in slide-in-from-right",
        )}
      >
        {isLoading || !alert ? (
          <div className="space-y-4 p-6">
            <div className="flex items-center justify-between">
              <Skeleton className="h-6 w-48" />
              <Button
                variant="ghost"
                size="icon"
                aria-label={isWorkspace ? "Back to alert queue" : "Close"}
                onClick={onClose}
                className={cn(isWorkspace && "lg:hidden")}
              >
                {isWorkspace ? <ArrowLeft className="h-4 w-4" /> : <X className="h-4 w-4" />}
              </Button>
            </div>
            <Skeleton className="h-8 w-32" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-start justify-between border-b p-4">
              {!authPresentation?.isBruteForce ? <div className="flex items-center gap-2 pr-2">
                <Badge variant={severityVariant(alert.severity)}>
                  {alert.severity}
                </Badge>
                <h2 className="text-lg font-semibold leading-tight">
                  {alert.title}
                </h2>
              </div> : <span className="sr-only">Authentication attack details</span>}
              <Button
                variant="ghost"
                size="icon"
                aria-label={isWorkspace ? "Back to alert queue" : "Close"}
                onClick={onClose}
                className={cn("shrink-0", isWorkspace && "lg:hidden")}
              >
                {isWorkspace ? <ArrowLeft className="h-4 w-4" /> : <X className="h-4 w-4" />}
              </Button>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto">
              {authPresentation?.isBruteForce && (
                <div className="border-b p-4">
                  <BruteForceSummary alert={alert} />
                </div>
              )}

              {/* Status bar */}
              {authPresentation?.successStatus !== "success" && <div className="flex items-center gap-2 border-b px-4 py-3">
                <span
                  className={cn(
                    "rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize",
                    statusBgColors[alert.status] ?? "bg-muted text-muted-foreground",
                  )}
                >
                  {authPresentation?.isBruteForce && authPresentation.firingCount > 1
                    ? "recurring"
                    : alert.status}
                </span>
                {alert.notification_suppressed && (
                  <Badge
                    variant="outline"
                    className="gap-1 text-muted-foreground"
                    title="A notification for this alert was suppressed by an active mute"
                  >
                    <BellOff className="h-3 w-3" />
                    Muted
                  </Badge>
                )}
                <div className={cn("ml-auto flex gap-2", isWorkspace && "hidden md:flex")}>
                  {canMute && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (authPresentation?.isBruteForce) setMuteDuration("30");
                        setMuteOpen(true);
                      }}
                    >
                      <BellOff className="mr-1.5 h-3.5 w-3.5" />
                      Mute rule
                    </Button>
                  )}
                  {alert.status === "new" && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleStatusUpdate("acknowledged")}
                      disabled={updateStatus.isPending}
                    >
                      <Eye className="mr-1.5 h-3.5 w-3.5" />
                      Acknowledge
                    </Button>
                  )}
                  {alert.status !== "resolved" && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleStatusUpdate("resolved")}
                      disabled={updateStatus.isPending}
                    >
                      <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
                      Resolve
                    </Button>
                  )}
                </div>
              </div>}

              <div className="border-b px-4 py-3">
                <div className="flex flex-wrap items-center gap-2">
                  <UserRound className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Owner</span>
                  {canAssign ? (
                    <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
                      {currentAssignee && alert.assigned_to !== currentAssignee.id && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => assignAlert.mutate({ alertId: alert.id, userId: currentAssignee.id })}
                          disabled={assignAlert.isPending}
                        >
                          Assign to me
                        </Button>
                      )}
                      <select
                        aria-label="Alert owner"
                        value={alert.assigned_to ?? ""}
                        onChange={(event) =>
                          assignAlert.mutate({
                            alertId: alert.id,
                            userId: event.target.value || null,
                          })
                        }
                        disabled={assignAlert.isPending}
                        className="h-9 max-w-[220px] rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
                      >
                        <option value="">Unassigned</option>
                        {alert.assigned_to &&
                          !assignees.some((tenantUser) => tenantUser.id === alert.assigned_to) && (
                            <option value={alert.assigned_to}>Assigned analyst</option>
                          )}
                        {assignees.map((tenantUser) => (
                          <option key={tenantUser.id} value={tenantUser.id}>
                            {tenantUser.name || tenantUser.email}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <span className="ml-auto text-sm text-muted-foreground">
                      {assignees.find((tenantUser) => tenantUser.id === alert.assigned_to)?.name ||
                        assignees.find((tenantUser) => tenantUser.id === alert.assigned_to)?.email ||
                        (alert.assigned_to ? "Assigned analyst" : "Unassigned")}
                    </span>
                  )}
                </div>
                {assignAlert.isError && (
                  <p className="mt-2 text-right text-xs text-destructive">
                    Could not update the alert owner. Please try again.
                  </p>
                )}
              </div>

              {isWorkspace && (
                <>
                  <ProgressRail alert={alert} />
                  <div
                    className="grid grid-cols-4 border-b border-border px-2 sm:px-4"
                    role="tablist"
                    aria-label="Alert detail sections"
                  >
                    {([
                      { value: "summary", label: "Summary" },
                      { value: "evidence", label: "Evidence", count: alert.matched_events.length },
                      { value: "activity", label: "Activity", count: 1 + (alert.notes?.length ?? 0) },
                      { value: "ai", label: "AI context" },
                    ] as Array<{ value: DetailTab; label: string; count?: number }>).map((tab) => (
                      <button
                        key={tab.value}
                        id={`alert-tab-${tab.value}`}
                        type="button"
                        role="tab"
                        aria-selected={activeTab === tab.value}
                        aria-controls={`alert-panel-${tab.value}`}
                        onClick={() => setActiveTab(tab.value)}
                        className={cn(
                          "relative flex min-h-11 min-w-0 items-center justify-center gap-1 px-1 text-[11px] font-medium sm:px-3 sm:text-sm",
                          activeTab === tab.value ? "text-foreground" : "text-muted-foreground",
                        )}
                      >
                        <span className="truncate">{tab.label}</span>
                        {tab.count !== undefined && (
                          <span className="rounded-full bg-secondary px-1.5 py-0.5 text-[10px]">
                            {tab.count}
                          </span>
                        )}
                        {activeTab === tab.value && (
                          <span className="absolute inset-x-2 bottom-0 h-0.5 bg-primary" />
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {(authPresentation?.successStatus !== "success" || isWorkspace) && <div
                id={isWorkspace ? `alert-panel-${activeTab}` : undefined}
                role={isWorkspace ? "tabpanel" : undefined}
                aria-labelledby={isWorkspace ? `alert-tab-${activeTab}` : undefined}
                tabIndex={isWorkspace ? 0 : undefined}
                className={cn("space-y-4 p-4 outline-none", isWorkspace && "pb-28 md:pb-6")}
              >
                {/* Details grid */}
                {(!isWorkspace || activeTab === "summary") && <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-muted-foreground">Source Type</p>
                    <p className="font-medium">{alert.source_type || "---"}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Dedup Key</p>
                    <p className="truncate font-mono text-xs">{alert.dedup_key}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">First Seen</p>
                    <p className="font-medium">{formatAbsoluteTime(alert.first_seen_at)}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatRelativeTime(alert.first_seen_at)}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Last Seen</p>
                    <p className="font-medium">{formatAbsoluteTime(alert.last_seen_at)}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatRelativeTime(alert.last_seen_at)}
                    </p>
                  </div>
                </div>}

                {/* Description */}
                {(!isWorkspace || activeTab === "summary") && alert.description && (
                  <div>
                    <h3 className="mb-1 text-sm font-medium text-muted-foreground">
                      Description
                    </h3>
                    <p className="text-sm">{alert.description}</p>
                  </div>
                )}

                {/* AI Analysis */}
                {(!isWorkspace || activeTab === "ai") && !authPresentation?.isBruteForce && <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-sm font-medium">
                      <Brain className="h-4 w-4 text-primary" />
                      AI Analysis
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {alert.enrichment_status === "pending" && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="h-4 w-4 shrink-0" />
                        <span>Analysis pending</span>
                      </div>
                    )}
                    {!alert.enrichment_status && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <CircleDashed className="h-4 w-4 shrink-0" />
                        <span>No AI analysis — unavailable or not started</span>
                      </div>
                    )}
                    {alert.enrichment_status === "failed" && (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm text-destructive">
                          <AlertTriangle className="h-4 w-4 shrink-0" />
                          <span>Analysis unavailable — manual review required</span>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={retryEnrichment.isPending}
                          onClick={() => retryEnrichment.mutate({ alertId: alert.id })}
                        >
                          <RotateCw
                            className={cn(
                              "mr-2 h-4 w-4",
                              retryEnrichment.isPending && "animate-spin",
                            )}
                          />
                          {retryEnrichment.isPending ? "Retrying…" : "Retry analysis"}
                        </Button>
                        {retryEnrichment.isError && (
                          <p className="text-sm text-destructive">
                            Failed to start retry. Please try again.
                          </p>
                        )}
                      </div>
                    )}
                    {alert.enrichment_status === "complete" && alert.ai_context && (
                      <div className="space-y-3 text-sm">
                        <div>
                          <p className="mb-1 font-medium">Summary</p>
                          <p className="whitespace-pre-wrap text-muted-foreground">
                            {alert.ai_context.summary}
                          </p>
                        </div>
                        <div>
                          <p className="mb-1 font-medium">Severity Reasoning</p>
                          <p className="whitespace-pre-wrap text-muted-foreground">
                            {alert.ai_context.severity_reasoning}
                          </p>
                        </div>
                        {alert.ai_context.recommended_actions.length > 0 && (
                          <div>
                            <p className="mb-1 font-medium">Recommended Actions</p>
                            <ul className="list-inside list-disc space-y-1 text-muted-foreground">
                              {alert.ai_context.recommended_actions.map((action, i) => (
                                <li key={i}>{action}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {alert.ai_context.mitre_technique && (
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">MITRE Technique</span>
                            <Badge variant="outline" className="font-mono text-xs">
                              {alert.ai_context.mitre_technique}
                            </Badge>
                          </div>
                        )}
                      </div>
                    )}
                    {alert.enrichment_status === "complete" && !alert.ai_context && (
                      <p className="text-sm text-muted-foreground">
                        Analysis completed but no data available
                      </p>
                    )}
                  </CardContent>
                </Card>}
                {isWorkspace && activeTab === "ai" && authPresentation?.isBruteForce && (
                  <Card>
                    <CardContent className="pt-6 text-sm text-muted-foreground">
                      Authentication-specific analysis is summarized in the attack overview above.
                      Validate the correlated sign-in evidence before taking action.
                    </CardContent>
                  </Card>
                )}
                {/* Rule Context */}
                {(!isWorkspace || activeTab === "summary") && !authPresentation?.isBruteForce &&
                  alert.context &&
                  Object.keys(alert.context).filter(
                    (k) => !LEGACY_AI_CONTEXT_KEYS.includes(k),
                  ).length > 0 && (
                  <div>
                    <h3 className="mb-2 text-sm font-medium text-muted-foreground">
                      Rule Context
                    </h3>
                    <div className="space-y-1 text-sm">
                      {Object.entries(alert.context)
                        .filter(([key]) => !LEGACY_AI_CONTEXT_KEYS.includes(key))
                        .map(([key, value]) => (
                          <div key={key} className="flex justify-between gap-2">
                            <span className="text-muted-foreground">
                              {key.replace(/_/g, " ")}
                            </span>
                            <span className="text-right font-medium">
                              {typeof value === "string"
                                ? value
                                : JSON.stringify(value)}
                            </span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Entities */}
                {(!isWorkspace || activeTab === "summary") && !authPresentation?.isBruteForce && alert.entities && Object.keys(alert.entities).length > 0 && (
                  <div>
                    <h3 className="mb-2 text-sm font-medium text-muted-foreground">
                      Entities
                    </h3>
                    <div className="space-y-2">
                      {Object.entries(alert.entities).map(([group, values]) => (
                        <div key={group}>
                          <p className="mb-1 text-xs font-medium uppercase text-muted-foreground">
                            {group}
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {values.map((val) => (
                              <Badge key={val} variant="secondary" className="font-mono text-xs">
                                {val}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Matched Events */}
                {(!isWorkspace || activeTab === "evidence") && !authPresentation?.isBruteForce && (
                  <div>
                    <EvidenceTimeline alert={alert} />
                    {alert.matched_events.length > 0 && <>
                    <button
                      onClick={() => setEventsExpanded(!eventsExpanded)}
                      className="mt-5 flex w-full items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
                    >
                      {eventsExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                      Matched Events ({alert.matched_events.length})
                    </button>
                    {eventsExpanded && (
                      <pre className="mt-2 max-h-96 overflow-auto rounded bg-muted p-3 text-xs">
                        {alert.matched_events.length > 50
                          ? JSON.stringify(alert.matched_events.slice(0, 50), null, 2) +
                            `\n\n... and ${alert.matched_events.length - 50} more events`
                          : JSON.stringify(alert.matched_events, null, 2)}
                      </pre>
                    )}
                    </>}
                  </div>
                )}

                {/* Analyst notes */}
                {(!isWorkspace || activeTab === "activity") && <div className="border-t pt-4" id="notes">
                  {isWorkspace && <AlertActivityTimeline alert={alert} />}
                  <h3 className="mb-2 text-sm font-medium text-muted-foreground">
                    Analyst notes
                  </h3>
                  {alert.notes && alert.notes.length > 0 ? (
                    <div className="space-y-2">
                      {[...alert.notes].reverse().map((note, index) => {
                        const tenantAuthor = assignees.find(
                          (tenantUser) => tenantUser.id === note.user_id,
                        );
                        const author =
                          tenantAuthor?.name ||
                          tenantAuthor?.email ||
                          note.author_email ||
                          (note.user_id === user?.sub ? user.email : null) ||
                          "Analyst";
                        return (
                          <article
                            key={`${note.created_at}-${index}`}
                            className="rounded-lg border bg-muted/30 p-3"
                          >
                            <div className="flex items-center justify-between gap-3 text-xs">
                              <span className="font-medium">{author}</span>
                              <time
                                dateTime={note.created_at}
                                className="shrink-0 text-muted-foreground"
                              >
                                {formatAbsoluteTime(note.created_at)}
                              </time>
                            </div>
                            <p className="mt-2 whitespace-pre-wrap text-sm">{note.text}</p>
                          </article>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No analyst notes yet.</p>
                  )}

                  {canAssign && (
                    <div className="mt-3 space-y-2">
                      <Textarea
                        value={noteText}
                        onChange={(event) => setNoteText(event.target.value)}
                        aria-label="Add analyst note"
                        placeholder="Record investigation context, decisions, or next steps"
                        maxLength={10000}
                      />
                      <div className="flex items-center justify-end gap-3">
                        {addNote.isError && (
                          <p className="mr-auto text-xs text-destructive">
                            Could not save the note. Please try again.
                          </p>
                        )}
                        <Button
                          size="sm"
                          onClick={handleAddNote}
                          disabled={!noteText.trim() || addNote.isPending}
                        >
                          <MessageSquarePlus className="mr-1.5 h-3.5 w-3.5" />
                          {addNote.isPending ? "Saving…" : "Add note"}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>}

                {/* Feedback */}
                {(!isWorkspace || activeTab === "activity") && <div className="border-t pt-4">
                  <h3 className="mb-2 text-sm font-medium text-muted-foreground">
                    Feedback
                  </h3>
                  <div className="flex gap-2">
                    <Button
                      variant={alert.feedback === "true_positive" ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleFeedback("true_positive")}
                      disabled={feedbackMutation.isPending}
                    >
                      <ThumbsUp className="mr-1.5 h-3.5 w-3.5" />
                      True Positive
                    </Button>
                    <Button
                      variant={alert.feedback === "false_positive" ? "destructive" : "outline"}
                      size="sm"
                      onClick={() => handleFeedback("false_positive")}
                      disabled={feedbackMutation.isPending}
                    >
                      <ThumbsDown className="mr-1.5 h-3.5 w-3.5" />
                      False Positive
                    </Button>
                  </div>
                  {alert.feedback_note && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      {alert.feedback_note}
                    </p>
                  )}
                </div>}
              </div>}
            </div>

            {isWorkspace &&
              authPresentation?.successStatus !== "success" &&
              ((canAssign && currentAssignee && alert.assigned_to !== currentAssignee.id) ||
                alert.status !== "resolved") && (
              <div className="fixed inset-x-0 bottom-[68px] z-20 flex items-center gap-2 border-t border-border bg-background/95 p-3 backdrop-blur md:hidden">
                {canAssign && currentAssignee && alert.assigned_to !== currentAssignee.id && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => assignAlert.mutate({ alertId: alert.id, userId: currentAssignee.id })}
                    disabled={assignAlert.isPending}
                  >
                    Assign to me
                  </Button>
                )}
                {alert.status === "new" && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleStatusUpdate("acknowledged")}
                    disabled={updateStatus.isPending}
                  >
                    Acknowledge
                  </Button>
                )}
                {alert.status !== "resolved" && (
                  <Button
                    size="sm"
                    className="flex-1"
                    onClick={() => handleStatusUpdate("resolved")}
                    disabled={updateStatus.isPending}
                  >
                    Resolve
                  </Button>
                )}
              </div>
            )}

            {/* #665: mute-rule dialog */}
            <Dialog open={muteOpen} onOpenChange={setMuteOpen}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Mute this rule</DialogTitle>
                  <DialogDescription>
                    Stops notifications for this rule on every channel. Detection keeps running
                    and alerts stay in history — only the outbound notification is suppressed.
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                  <div>
                    <Label htmlFor="mute-duration">Duration</Label>
                    <select
                      id="mute-duration"
                      value={muteDuration}
                      onChange={(e) => setMuteDuration(e.target.value)}
                      className={MUTE_SELECT_CLASS}
                    >
                      {MUTE_DURATIONS.filter((d) => !authPresentation?.isBruteForce || d.value === "30").map((d) => (
                        <option key={d.value} value={d.value}>
                          {d.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="mute-reason">Reason (optional)</Label>
                    <Textarea
                      id="mute-reason"
                      value={muteReason}
                      onChange={(e) => setMuteReason(e.target.value)}
                      placeholder="e.g. public SSH is accepted for this environment"
                      className="mt-1.5"
                    />
                  </div>
                  {mutePolicy.isError && (
                    <p className="text-sm text-destructive">
                      Could not mute the rule. Please try again.
                    </p>
                  )}
                </div>

                <DialogFooter>
                  <Button variant="ghost" onClick={() => setMuteOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleMute} disabled={mutePolicy.isPending}>
                    {mutePolicy.isPending && (
                      <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                    )}
                    Mute rule
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}
      </div>
    </>
  );
}

function ProgressRail({ alert }: { alert: Alert }) {
  const steps = [
    { label: "Detected", detail: formatRelativeTime(alert.created_at), complete: true },
    {
      label: "AI analyzed",
      detail: alert.enrichment_status === "complete" ? "Complete" : alert.enrichment_status ?? "Not started",
      complete: alert.enrichment_status === "complete",
    },
    {
      label: "Assigned",
      detail: alert.assigned_to ? "Owned" : "Unassigned",
      complete: Boolean(alert.assigned_to),
    },
    {
      label: "Reviewed",
      detail: alert.status === "new" ? "Pending" : "Complete",
      complete: alert.status !== "new",
    },
    {
      label: "Resolved",
      detail: alert.status === "resolved" ? "Complete" : "Open",
      complete: alert.status === "resolved",
    },
  ];

  return (
    <ol className="grid grid-cols-5 gap-1 border-b border-border px-4 py-3" aria-label="Alert triage progress">
      {steps.map((step, index) => (
        <li key={step.label} className="relative min-w-0 text-center">
          {index > 0 && (
            <span
              className={cn(
                "absolute right-1/2 top-2.5 h-px w-full",
                step.complete ? "bg-primary/60" : "bg-border",
              )}
              aria-hidden
            />
          )}
          <span
            className={cn(
              "relative z-10 mx-auto flex h-5 w-5 items-center justify-center rounded-full border bg-background",
              step.complete ? "border-primary text-primary" : "border-border text-muted-foreground",
            )}
          >
            {step.complete ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3 w-3" />}
          </span>
          <p className="mt-1 truncate text-[10px] font-medium sm:text-xs">{step.label}</p>
          <p className="hidden truncate text-[10px] capitalize text-muted-foreground sm:block">{step.detail}</p>
        </li>
      ))}
    </ol>
  );
}

function EvidenceTimeline({ alert }: { alert: Alert }) {
  const events = alert.matched_events.length > 0
    ? alert.matched_events
    : [{ action: "Alert correlated", timestamp: alert.created_at }];

  return (
    <section aria-labelledby="evidence-timeline-title">
      <div className="flex items-center gap-2">
        <FileJson className="h-4 w-4 text-primary" />
        <h3 id="evidence-timeline-title" className="text-sm font-medium">Evidence timeline</h3>
      </div>
      <ol className="mt-3">
        {events.slice(0, 10).map((event, index) => (
          <li key={index} className="relative flex gap-3 pb-5 last:pb-0">
            {index < Math.min(events.length, 10) - 1 && (
              <span className="absolute left-[13px] top-7 h-[calc(100%-1.2rem)] w-px bg-border" aria-hidden />
            )}
            <span className="z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-primary/40 bg-background text-primary">
              {index === events.length - 1 ? <AlertTriangle className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline gap-x-3">
                <time className="text-xs font-medium text-muted-foreground">{eventTime(event, alert, index)}</time>
                <p className="text-sm font-semibold">{eventTitle(event, index)}</p>
              </div>
              <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-muted-foreground">
                {eventDetail(event)}
              </p>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

function AlertActivityTimeline({ alert }: { alert: Alert }) {
  return (
    <section className="mb-5" aria-labelledby="alert-activity-title">
      <div className="flex items-center gap-2">
        <Activity className="h-4 w-4 text-primary" />
        <h3 id="alert-activity-title" className="text-sm font-medium">Activity</h3>
      </div>
      <ol className="ml-3 mt-3 space-y-4 border-l border-border pl-5">
        {alert.status !== "new" && (
          <li className="relative">
            <span className="absolute -left-[1.65rem] top-1 h-2 w-2 rounded-full bg-primary" />
            <p className="text-sm">Status changed to <span className="font-medium capitalize">{alert.status}</span></p>
            <time className="text-xs text-muted-foreground">{formatAbsoluteTime(alert.updated_at)}</time>
          </li>
        )}
        {alert.assigned_to && (
          <li className="relative">
            <span className="absolute -left-[1.65rem] top-1 h-2 w-2 rounded-full bg-blue-400" />
            <p className="text-sm">Alert assigned to an analyst</p>
            <p className="text-xs text-muted-foreground">Current owner</p>
          </li>
        )}
        <li className="relative">
          <span className="absolute -left-[1.65rem] top-1 h-2 w-2 rounded-full bg-muted-foreground" />
          <p className="text-sm">Alert detected and added to the queue</p>
          <time className="text-xs text-muted-foreground">{formatAbsoluteTime(alert.created_at)}</time>
        </li>
      </ol>
    </section>
  );
}

function eventTitle(event: Record<string, unknown>, index: number): string {
  for (const key of ["action", "event_name", "event_type", "type", "source"]) {
    const value = event[key];
    if (typeof value === "string" && value.trim()) return value;
  }
  return `Matched event ${index + 1}`;
}

function eventDetail(event: Record<string, unknown>): string {
  const excluded = new Set(["time", "timestamp", "created_at", "action", "event_name", "event_type", "type"]);
  const details = Object.entries(event)
    .filter(([key]) => !excluded.has(key))
    .slice(0, 4)
    .map(([key, value]) => `${key.replace(/_/g, " ")}: ${displayEventValue(value)}`);
  return details.join(" · ") || "Source event retained with this alert.";
}

function displayEventValue(value: unknown): string {
  if (Array.isArray(value)) return value.join(", ");
  if (value && typeof value === "object") return JSON.stringify(value);
  return String(value ?? "—");
}

function eventTime(event: Record<string, unknown>, alert: Alert, index: number): string {
  for (const key of ["time", "timestamp", "created_at"]) {
    const value = event[key];
    if (typeof value === "string") return formatAbsoluteTime(value);
  }
  return formatAbsoluteTime(index === 0 ? alert.first_seen_at : alert.last_seen_at);
}
